import {
  skyFlightFrame,
  skyFlightPointToWorld,
} from '../../../packages/domain/src/92-sky-flight-frame.mjs';

const phases = new Set(['entry', 'throat', 'chamber', 'breakout']);
const BUDGET = Object.freeze({ wisps: 24, balanced: 12, guides: 26 });

/** Sample wall-side steam/dust in hull-local metres from the actual floor
 * descriptors. Six soft steam wisps and18 dust motes share one finite pool;
 * every identity fades before recycling. No surface/route/contact is changed.
 * Missing floors or nonfinite time return null, never a guessed corridor. */
export function skyInteriorAtmospherePose(live, index, time) {
  const chamber = index >= 12,
    floor = live?.walls?.find(
      (wall) => wall.id === (chamber ? 'chamber-height--1' : 'throat-floor'),
    );
  if (!floor || !Number.isFinite(time)) return null;
  const steam = index % 4 === 0,
    side = index % 2 ? -1 : 1,
    phase = (((time / (steam ? 5.8 : 8.5) + index * 0.61803398875) % 1) + 1) % 1,
    y = floor.center.y + floor.halfExtents.y,
    x = chamber ? floor.halfExtents.x - 0.75 : floor.halfExtents.x - 0.7;
  return {
    x: side * (x + Math.sin(phase * Math.PI * 2 + index) * 0.04),
    y: y + 0.28 + phase * (steam ? 0.65 : 1.15),
    z: floor.center.z + (((index * 0.38196601125) % 1) * 2 - 1) * (floor.halfExtents.z - 1.1),
    width: steam ? 0.38 : 0.025,
    height: steam ? 0.62 : 0.025,
    opacity: Math.sin(phase * Math.PI) ** 2 * (steam ? 0.13 : 0.3),
    steam,
  };
}

/** Two fixed instanced draws, at most100 triangles. Warm floor-edge inlays
 * follow the real floor slabs and remain steady in reduced motion. They are
 * architectural illumination, never arrows or a promise that a moving gate is
 * clear. Depth-tested steam stays at lower walls, outside the pilot corridor;
 * minimal/reduced settings omit particles without darkening the guide trim. */
export function createSkyInteriorAtmosphere(T, scene) {
  const geometry = new T.PlaneGeometry(1, 1),
    guideGeometry = new T.PlaneGeometry(1, 1),
    alpha = new T.InstancedBufferAttribute(new Float32Array(BUDGET.wisps), 1),
    steamMaterial = new T.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: false,
      vertexShader: `
        attribute float wispAlpha;
        varying vec2 vUv;
        varying float vAlpha;
        varying float vDepth;
        void main() {
          vec4 p = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
          p.xy += position.xy * vec2(length(instanceMatrix[0].xyz), length(instanceMatrix[1].xyz));
          vUv = uv; vAlpha = wispAlpha; vDepth = -p.z;
          gl_Position = projectionMatrix * p;
        }`,
      fragmentShader: `
        varying vec2 vUv;
        varying float vAlpha;
        varying float vDepth;
        void main() {
          vec2 p = vUv * 2.0 - 1.0;
          float soft = pow(max(0.0, 1.0 - dot(p, p)), 2.0);
          float opacity = soft * vAlpha * smoothstep(1.4, 2.8, vDepth);
          if (opacity < 0.005) discard;
          gl_FragColor = vec4(0.79, 0.69, 0.5, opacity);
        }`,
    }),
    guideMaterial = new T.MeshBasicMaterial({
      color: '#f9c47b',
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
      depthTest: true,
      side: T.DoubleSide,
      toneMapped: false,
    });
  geometry.setAttribute('wispAlpha', alpha);
  const wisps = new T.InstancedMesh(geometry, steamMaterial, BUDGET.wisps),
    guides = new T.InstancedMesh(guideGeometry, guideMaterial, BUDGET.guides),
    matrix = new T.Matrix4(),
    hull = new T.Matrix4(),
    rotation = new T.Matrix4().makeRotationX(-Math.PI / 2);
  wisps.name = 'sky-interior-steam-dust';
  guides.name = 'sky-interior-floor-inlays';
  for (const mesh of [wisps, guides]) {
    mesh.count = 0;
    mesh.frustumCulled = false;
    scene.add(mesh);
  }
  let dead = false;
  return {
    draw(state, decoration, quiet) {
      const live = state.leviathan,
        frame = skyFlightFrame(live?.hullFrame);
      const active =
        !dead &&
        state.sector < 2 &&
        ['playing', 'paused'].includes(state.status) &&
        frame &&
        phases.has(live?.encounter?.phase);
      wisps.count = guides.count = 0;
      if (!active) return;
      const { right: r, up: u, back: b, origin: o } = frame;
      hull.set(r.x, u.x, b.x, o.x, r.y, u.y, b.y, o.y, r.z, u.z, b.z, o.z, 0, 0, 0, 1);
      const count =
        quiet || decoration.tier >= 2 ? 0 : decoration.tier === 0 ? BUDGET.wisps : BUDGET.balanced;
      for (let index = 0; index < count; index++) {
        const pose = skyInteriorAtmospherePose(live, index, state.time);
        if (!pose) continue;
        const world = skyFlightPointToWorld(frame, pose);
        matrix.makeScale(pose.width, pose.height, 1);
        matrix.setPosition(world.x, world.y, world.z);
        wisps.setMatrixAt(wisps.count, matrix);
        alpha.setX(wisps.count++, pose.opacity);
      }
      for (const id of ['throat-floor', 'chamber-height--1']) {
        const floor = live.walls.find((wall) => wall.id === id);
        if (!floor) continue;
        const stations = id === 'throat-floor' ? 8 : 5,
          edge = floor.halfExtents.x - (id === 'throat-floor' ? 0.58 : 0.75);
        for (const side of [-1, 1])
          for (let i = 0; i < stations; i++) {
            const z = floor.center.z - floor.halfExtents.z + 1 + i * 2;
            matrix.makeScale(0.035, 0.75, 1).premultiply(rotation);
            matrix.setPosition(side * edge, floor.center.y + floor.halfExtents.y + 0.015, z);
            matrix.premultiply(hull);
            guides.setMatrixAt(guides.count++, matrix);
          }
      }
      wisps.instanceMatrix.needsUpdate =
        guides.instanceMatrix.needsUpdate =
        alpha.needsUpdate =
          true;
    },
    dispose() {
      if (dead) return;
      dead = true;
      for (const mesh of [wisps, guides]) {
        mesh.removeFromParent();
        mesh.dispose();
      }
      for (const resource of [geometry, guideGeometry, steamMaterial, guideMaterial])
        resource.dispose();
    },
  };
}
